import { pick, values, without } from 'lodash-es'
import { isUrl, isWikidataItemUri, isWikidataPropertyUri } from '#app/lib/boolean_tests'
import { objectEntries } from '#app/lib/utils'
import { authorRoleProperties } from '#entities/lib/editor/properties_per_subtype'
import { getPropertyDatatype } from '#entities/lib/editor/properties_per_type'
import { getEntitiesByUris, type SerializedEntity } from '#entities/lib/entities'
import type { ClaimValueTypeByDatatype, EntityUri, WdPropertyUri, WdEntityUri, InvClaimValue, EntityValue } from '#server/types/entity'
import type { PropertyDatatype } from '#server/types/property'
import type { WikimediaLanguageCode } from 'wikibase-sdk'

interface ImportedClaim <T extends PropertyDatatype = PropertyDatatype> {
  property: WdPropertyUri
  value: ClaimValueTypeByDatatype[T]
  datatype: T
  checked: boolean
}

export interface InvEntityImportableData {
  labels: { lang: WikimediaLanguageCode, label: string, checked: boolean }[]
  claims: ImportedClaim[]
  invEntity: SerializedEntity
  wdEntity: SerializedEntity
  total: number
}

export async function getInvEntityImportableData (invEntityUri: EntityUri, wdEntityUri: WdEntityUri) {
  const entities = await getEntitiesByUris({ uris: [ invEntityUri, wdEntityUri ], refresh: true })
  const invEntity = entities[invEntityUri]
  const wdEntity = entities[wdEntityUri]

  const importData: InvEntityImportableData = {
    labels: [],
    claims: [],
    invEntity,
    wdEntity,
    total: 0,
  }

  for (const [ lang, label ] of objectEntries(invEntity.labels)) {
    if (wdEntity.labels[lang] == null && wdEntity.labels.mul !== label) {
      importData.labels.push({ lang: lang as WikimediaLanguageCode, label, checked: true })
    }
  }

  for (let [ property, propertyClaims ] of objectEntries(invEntity.claims)) {
    if (isWikidataPropertyUri(property) && !ignoredProperties.includes(property)) {
      const datatype = getPropertyDatatype(property)
      let importablePropertyClaims: InvClaimValue[] = []
      if (property === 'wdt:P50') {
        propertyClaims = dropAuthorsWithOtherRoles(wdEntity, propertyClaims as EntityValue[])
      }
      if (wdEntity.claims[property] != null) {
        if (datatype === 'url') {
          importablePropertyClaims = filterOutSimilarUrls(propertyClaims as string[], wdEntity.claims[property] as string[])
        } else if (datatype === 'external-id' || datatype === 'entity') {
          importablePropertyClaims = without(propertyClaims, ...wdEntity.claims[property])
        } else {
          // Ignore other claims for which a value already exists
          importablePropertyClaims = []
        }
      } else {
        importablePropertyClaims = propertyClaims
      }
      for (const value of importablePropertyClaims) {
        if (datatype === 'entity') {
          // Links to Inventaire entities can't be imported to Wikidata
          if (isWikidataItemUri(value)) importData.claims.push({ property, value, datatype, checked: true })
        } else {
          importData.claims.push({ property, value, datatype, checked: true })
        }
      }
    }
  }

  importData.total = importData.labels.length + importData.claims.length

  return importData
}

// Do not import authors as wdt:P50 authors, when they already have another role
function dropAuthorsWithOtherRoles (wdEntity: SerializedEntity, propertyClaims: EntityValue[]) {
  const authorValues = values(pick(wdEntity.claims, authorRoleProperties)).flat()
  return without(propertyClaims, ...authorValues)
}

const ignoredProperties = [
  'wdt:P31',
]

function filterOutSimilarUrls (invUrls: string[], wdUrls: string[]) {
  const importableUrls: string[] = []
  const wdValidUrls = wdUrls.filter(isUrl)
  const invValidUrls = invUrls.filter(isUrl)
  for (const invUrl of invValidUrls) {
    const { host: invHost, pathname: invPathname } = new URL(invUrl)
    const someWdUrlMatches = wdValidUrls.some(wdUrl => {
      const { host: wdHost, pathname: wdPathname } = new URL(wdUrl)
      return invHost === wdHost && normalizePathname(invPathname) === normalizePathname(wdPathname)
    })
    if (!someWdUrlMatches) importableUrls.push(invUrl)
  }
  return importableUrls
}

const normalizePathname = (str: string) => str.replace(/\/$/, '').normalize().trim()
